import dayjs from "dayjs";

/** Keys the calendar grid handles for roving-focus navigation (WAI-ARIA APG
 *  grid pattern). Selection (Enter/Space) stays on the focused cell itself. */
export type CalendarNavKey =
  | "ArrowLeft"
  | "ArrowRight"
  | "ArrowUp"
  | "ArrowDown"
  | "Home"
  | "End"
  | "PageUp"
  | "PageDown";

const NAV_KEYS = new Set<string>([
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
  "PageUp",
  "PageDown",
]);

export const isCalendarNavKey = (key: string): key is CalendarNavKey =>
  NAV_KEYS.has(key);

/** Start of the focused date's week, honoring `weekStartsOn` so Home lands on
 *  the leftmost cell of the rendered row (not dayjs's locale default). */
const startOfWeek = (date: dayjs.Dayjs, weekStartsOn: 0 | 1): dayjs.Dayjs =>
  date.subtract((date.day() - weekStartsOn + 7) % 7, "day");

// Independent of min/max/disabled: focus moves through disabled days (they
// reject selection but stay reachable), per the APG grid. PageUp/PageDown keep
// the day-of-month, relying on dayjs to clamp overflow (Jan 31 → Feb 28).
export const nextFocusedDate = (
  current: Date,
  key: CalendarNavKey,
  weekStartsOn: 0 | 1,
): Date => {
  const date = dayjs(current);
  switch (key) {
    case "ArrowLeft":
      return date.subtract(1, "day").toDate();
    case "ArrowRight":
      return date.add(1, "day").toDate();
    case "ArrowUp":
      return date.subtract(1, "week").toDate();
    case "ArrowDown":
      return date.add(1, "week").toDate();
    case "Home":
      return startOfWeek(date, weekStartsOn).toDate();
    case "End":
      return startOfWeek(date, weekStartsOn).add(6, "day").toDate();
    case "PageUp":
      return date.subtract(1, "month").toDate();
    case "PageDown":
      return date.add(1, "month").toDate();
  }
};
